import { DeviceType, TranslationScopes } from "@finbackoffice/enums";
import {
    ClientBFFClient,
    IGetUserInterfaceRequest,
    IUserInterfaceMapResponse,
} from "@finbackoffice/clientbff-client";

export async function fetchLocalTranslations(
    request: IGetUserInterfaceRequest,
    localOrigin?: string,
) {
    const origin = localOrigin || window.location.origin;
    const params = new URLSearchParams({
        language: request.language,
        scopes: request.scopes.join(","),
    });

    const response = await fetch(`${origin}/api/translations?` + params.toString(), {
        method: "GET",
    });

    if (!response.ok) {
        const filesList = request.scopes.map((s) => `translations/${s}.json`).join(",");

        throw new Error(
            `Failed to fetch local translations, make sure next files are valid: ${filesList}`,
        );
    }

    return response.json();
}

type IGetTranslationsConfig = {
    client: ClientBFFClient;
    request: IGetUserInterfaceRequest;
    includeLocalTranslations: boolean;
    deviceType: DeviceType;
    localOrigin?: string;
};

export async function getTranslations(
    config: IGetTranslationsConfig,
): Promise<IUserInterfaceMapResponse> {
    const { client, request, includeLocalTranslations, localOrigin, deviceType } = config;
    const translations =
        deviceType === "mobile"
            ? await client.getMobileTranslations(request)
            : await client.getDesktopTranslations(request);

    if (!includeLocalTranslations) {
        return translations;
    }

    const localTranslations = await fetchLocalTranslations(request, localOrigin);

    for (const scope in translations) {
        if (process.env.LOCAL_ONLY_TRANSLATIONS) {
            translations[scope as TranslationScopes] = {
                ...localTranslations[scope as TranslationScopes],
            };
        } else {
            translations[scope as TranslationScopes] = {
                ...localTranslations[scope as TranslationScopes],
                ...translations[scope as TranslationScopes],
            };
        }
    }

    return translations;
}

export async function getMobileTranslations(config: Omit<IGetTranslationsConfig, "deviceType">) {
    return getTranslations({ ...config, deviceType: DeviceType.Mobile });
}

export async function getDesktopTranslations(config: Omit<IGetTranslationsConfig, "deviceType">) {
    return getTranslations({ ...config, deviceType: DeviceType.Desktop });
}
